import { AceLogger } from '@logging/ace-logger';
import { Ticket } from '@shared/services';
import { AceLoggerService } from '@logging/ace-logger.service';
import { PartialTicket } from '../tickets.interface';


export class TicketsStore {

  private readonly logger: AceLogger;
  private readonly ticketsById: Map<string, Ticket> = new Map();

  constructor(logService: AceLoggerService) {
    this.logger = logService.create(TicketsStore.name);
  }

  public addTicket<T extends Ticket>(ticket: T): T {
    const ticketId = ticket.ticket_id;
    const existingTicket = <T>this.ticketsById.get(ticketId);
    if (existingTicket) {
      return this.mergeTickets(existingTicket, ticket);
    } else {
      this.logger.debug('Ticket with id ' + ticketId + ', type: ' + ticket.type + ' does not exist in cache, populate cache with current ticket.');
      this.ticketsById.set(ticketId, ticket);
      return ticket;
    }
  }

  public getTicketById(ticketId: string): Ticket {
    const ticket = this.ticketsById.get(ticketId);
    if (ticket) {
      return ticket;
    }
    throw new Error('Cannot find ticket with id ' + ticketId + ' in tickets store.');
  }

  private mergeTickets<T extends Ticket>(ticketInCache: T, ticket: PartialTicket): T {
    this.logger.debug('Update ticket with id ' + ticketInCache.ticket_id + ', type: ' + ticketInCache.type + ' with existing cached ticket.');
    return Object.assign(ticketInCache, ticket);
  }

  public updateTicket<T extends Ticket>(ticketId: string, ticket: Partial<T>): T | null {
    const existingTicket = <T>this.ticketsById.get(ticketId);
    if (existingTicket) {
      return this.mergeTickets(existingTicket, ticket);
    } else {
      this.logger.debug('Cannot find ticket with id ' + ticketId + ', type: ' + existingTicket.type + ', skip updating.');
    }
    return null;
  }

  public reset(): void {
    this.ticketsById.clear();
    this.logger.debug('All tickets cleared from ticket store.');
  }

}
